import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { createRealization, updateRealization, getRealizations } from '../services/apiService';
import ImageUploader from '../components/ImageUploader';

const RealizationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    category: 'CO2',
    realizationType: 'gravure',
    image: '',
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      fetchRealization();
    }
  }, [isEdit, id]);

  const fetchRealization = async () => {
    try {
      const realizations = await getRealizations();
      const realization = realizations.find(r => r.id === id);
      if (realization) {
        setFormData({
          title: realization.title || '',
          category: realization.category || 'CO2',
          realizationType: realization.realizationType || 'gravure',
          image: realization.image || '',
          description: realization.description || ''
        });
      }
    } catch (error) {
      console.error('Error fetching realization:', error);
      setError('Erreur lors du chargement');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEdit) {
        await updateRealization(id, formData);
      } else {
        await createRealization(formData);
      }

      navigate('/admin/dashboard?tab=realizations');
    } catch (err) {
      console.error('Error saving:', err);
      setError('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/admin/dashboard?tab=realizations')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour au dashboard
        </button>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-8" style={{ color: '#5C3A21' }}>
            {isEdit ? 'Modifier la réalisation' : 'Ajouter une réalisation'}
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2">Titre *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                placeholder="Signalétique bois personnalisée"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Catégorie *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                >
                  <option value="CO2">CO2</option>
                  <option value="Fibre">Fibre</option>
                  <option value="Mixte">Mixte</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Type *</label>
                <select
                  name="realizationType"
                  value={formData.realizationType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  data-testid="realization-type"
                >
                  <option value="gravure">Gravure</option>
                  <option value="decoupe">Découpe</option>
                  <option value="gravure_decoupe">Gravure & Découpe</option>
                </select>
              </div>
            </div>

            <div>
              <ImageUploader
                label="Image"
                required
                value={formData.image}
                onChange={(v) => setFormData((prev) => ({ ...prev, image: v }))}
                hint="Image de votre réalisation"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="4"
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                placeholder="Gravure laser haute précision sur bois massif..."
              />
            </div>

            <div className="flex items-center justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard?tab=realizations')}
                className="px-6 py-3 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-lg text-white font-semibold flex items-center space-x-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ backgroundColor: '#5C3A21' }}
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RealizationForm;
